import { useContext, useCallback, useEffect, useState } from 'react';
import { Button, Flex, Text, ICSSInJSStyle, Loader } from '@fluentui/react-northstar';
import ErrorAlert from '../components/errorAlert/ErrorAlert';
import { useTranslation } from 'react-i18next';
import { HostClientType } from '@microsoft/teams-js';
import { authentication } from '@microsoft/teams-js';
import HopexApi from '../api/HopexApi';
import AppContext from '../contexts/AppContext';
import { IDiagram } from '../models/IDiagram';
import { IHopexCredentials } from '../models/IHopexCredentials';

const urlparams = new URLSearchParams(window.location.search);
const urlCredentials = urlparams.get('credentials');
const urlDiagId = urlparams.get('diagid');

// styles
const styles: { [key: string]: ICSSInJSStyle } = {
    main: { width: '100%' },
    top: { width: '100%' },
}

const DiagramRoute = () => {
    const { t } = useTranslation();
    const [tabCredentials] = useState<IHopexCredentials>(urlCredentials ? JSON.parse(atob(urlCredentials)) : undefined);
    const [authenticating, setAuthenticating] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>();

    const { hopexContext, setHopexContext, teamsContext } = useContext(AppContext);
    const [diagramId] = useState<string | null>(urlDiagId);
    const [diagramUrl, setDiagramUrl] = useState<string>();
    const [diagram, setDiagram] = useState<IDiagram>();
    const [diagramPicture, setDiagramPicture] = useState<string>();
    const isMobile = teamsContext?.app.host.clientType === HostClientType.ios || teamsContext?.app.host.clientType === HostClientType.android;

    useEffect(() => {
        if (tabCredentials && !authenticating && !errorMessage && !hopexContext) {
            setAuthenticating(true);
            HopexApi.getContext(tabCredentials)
                .then((result) => {
                    if (result.data.data._currentContext) {
                        setHopexContext({
                            credentials: {
                                serverUrl: tabCredentials.serverUrl,
                                authenticationMethod: tabCredentials.authenticationMethod,
                                apiKey: tabCredentials.apiKey || '',
                                username: tabCredentials.username || '',
                                password: tabCredentials.password || '',
                            },
                            environment: result.data.data._currentContext,
                        });
                    }
                    else {
                        // Error ?
                        setAuthenticating(false);
                        setErrorMessage(t('Error.CannotLoginEditConfig'))
                    }
                }).catch((reason) => {
                    setErrorMessage(t('Error.CannotLoginEditConfig'))
                }).finally(() => {
                    setAuthenticating(false);
                });
        }
    }, [tabCredentials, authenticating, errorMessage, hopexContext, setHopexContext, t]);

    const generateDiagramDirectLink = useCallback((): string | undefined => {
        if (hopexContext && diagramId) {
            const teamsDesktopId = '(s8MR)byXH42';
            const diagramToolId = 'Fk(3zVHpEXWN';
            const objectIdParameterId = 'q0Co0cirIj0C';
            const currentUrl = encodeURIComponent(window.location.href);
            return `${hopexContext.credentials.serverUrl}${hopexContext.credentials.serverUrl.endsWith('/') ? '' : '/'}AuthTeams/hopex/?Db=${hopexContext.environment.databaseId}&Desktop=${teamsDesktopId}&Profile=${hopexContext.environment.profileId}&userdata=Tool-${diagramToolId}|${objectIdParameterId}-${diagramId}&teamsUrl=${currentUrl}`;
        }
        return undefined;
    }, [hopexContext, diagramId]);

    useEffect(() => {
        if (diagramId && hopexContext) {
            HopexApi.getDiagram(hopexContext.credentials, diagramId)
                .then((diagramsResp) => {
                    if (diagramsResp.data.data && diagramsResp.data.data.diagram && diagramsResp.data.data.diagram?.length > 0) {
                        const selectedDiagram = diagramsResp.data.data.diagram[0];

                        setDiagram(selectedDiagram);
                        setDiagramUrl(generateDiagramDirectLink());

                        const imageType = 'image/svg+xml';
                        HopexApi.getDiagramPicture(
                            hopexContext.credentials,
                            selectedDiagram.downloadUrl,
                            imageType,
                        ).then((response) => {
                            setDiagramPicture(response.data);
                        }).catch(() => {
                            setDiagramPicture(undefined);
                        });
                    }
                }).catch((reason) => {
                    //setErrorMessage('Error.CannotGetDiagrams');
                });
        }
    }, [diagramId, hopexContext, generateDiagramDirectLink]);

    /*
    // auth directly in frame
    const handleLogin = () => {
        if (diagramUrl && hopexContext) {
            document.location.assign(diagramUrl);
        }
    };
    */

    const handleWithPreLogin = () => {
        if (diagramUrl && hopexContext) {
            const redirectUrl = `${window.location.origin}/login/end?&Db=${hopexContext.environment.databaseId}&Profile=${hopexContext.environment.profileId}`;
            const url = `${hopexContext.credentials.serverUrl}/AuthTeams?redirectUrl=${encodeURI(redirectUrl)}`;

            authentication.authenticate({
                url: `/redirect?url=${btoa(url)}`,
                width: 500,
                height: 600,
                successCallback: function() {
                    document.location.assign(diagramUrl);
                },
                failureCallback: () => {
                    //setLoginMessage(`${t("DiagramTab.AuthenticationFailure")}: ${reason}`);
                }
            });
        }
    };

    return (<Flex column hAlign="center" gap={"gap.large"} styles={styles.main}>
        {authenticating && (
            <Loader label={t("DiagramTab.Loading")} />
        )}
        {hopexContext && diagram && (<>
            <Text content={diagram.name} as="h2" styles={{ marginTop: 20 }} />
        </>)}
        {diagramUrl && hopexContext && !isMobile && (<Flex gap="gap.medium">
            <Button content={t("DiagramTab.Connect")} primary onClick={handleWithPreLogin} />
        </Flex>)}
        {diagramPicture && (
            <div className="diagram-picture-container" dangerouslySetInnerHTML={{ __html: diagramPicture }}></div>
        )}

        { errorMessage && (<ErrorAlert message={errorMessage} />) }
    </Flex>)
}

export default DiagramRoute;
